import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  PlayCircleFilled,
  PauseCircleFilled,
  CaretRightFilled,
  PauseOutlined,
  SoundOutlined,
  SettingOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LoadingOutlined,
  BackwardOutlined,
  ForwardOutlined,
} from '@ant-design/icons';

// Format seconds into MM:SS or HH:MM:SS
const formatTime = (seconds) => {
  if (isNaN(seconds) || seconds === null) return '00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const formattedMins = mins < 10 ? `0${mins}` : mins;
  const formattedSecs = secs < 10 ? `0${secs}` : secs;

  if (hrs > 0) {
    return `${hrs}:${formattedMins}:${formattedSecs}`;
  }
  return `${formattedMins}:${formattedSecs}`;
};

// Styled Components
const PlayerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  user-select: none;
  font-family: 'Roboto', 'Inter', -apple-system, sans-serif;
  color: #fff;
  cursor: ${props => props.showControls ? 'default' : 'none'};
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: pointer;
`;

const BufferingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
`;

const LargePlayPauseIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 8;
  font-size: 32px;
  pointer-events: none;
  animation: rippleFade 0.6s ease-out forwards;

  @keyframes rippleFade {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
    30% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.1);
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(1.3);
    }
  }
`;

const ControlsOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
  padding: 12px 16px 8px;
  z-index: 12;
  transition: opacity 0.3s ease, transform 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
  transform: translateY(${props => props.visible ? '0' : '10px'});
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
`;

const TimelineContainer = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  margin-bottom: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: height 0.1s ease;

  &:hover {
    height: 8px;
  }
`;

const TimelineProgress = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ff0000; // YouTube Red!
  border-radius: 3px;
  width: ${props => props.percent}%;
`;

const TimelineHandle = styled.div`
  position: absolute;
  left: ${props => props.percent}%;
  transform: translateX(-50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff0000;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
  transition: transform 0.1s ease;
  pointer-events: none;

  ${TimelineContainer}:hover & {
    transform: translateX(-50%) scale(1.3);
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ControlButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: transform 0.15s, opacity 0.15s;

  &:hover {
    transform: scale(1.15);
    color: #ff0000;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover .volume-slider {
    width: 60px;
    opacity: 1;
  }
`;

const VolumeSlider = styled.input`
  width: 0;
  opacity: 0;
  height: 4px;
  -webkit-appearance: none;
  background: rgba(255, 255, 255, 0.3);
  outline: none;
  border-radius: 2px;
  transition: width 0.2s ease, opacity 0.2s ease;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 4px;
    cursor: pointer;
  }

  &::-webkit-slider-thumb {
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    -webkit-appearance: none;
    margin-top: -4px;
  }

  &::-moz-range-thumb {
    height: 12px;
    width: 12px;
    border: none;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
  }
`;

const TimeDisplay = styled.span`
  font-size: 13px;
  color: #ddd;
  font-variant-numeric: tabular-nums;
`;

const DropdownMenu = styled.div`
  position: absolute;
  bottom: 50px;
  right: ${props => props.right || '16px'};
  background: rgba(28, 28, 28, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 6px 0;
  min-width: 130px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  z-index: 15;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.active ? '#ff0000' : '#fff'};
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  padding: 8px 16px;
  text-align: left;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SpeedLabel = styled.span`
  background: rgba(255, 255, 255, 0.15);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
  margin-left: 6px;
`;

const YoutubeVideoPlayer = ({ src }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const timelineRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [quality, setQuality] = useState('Auto');
  const [isBuffering, setIsBuffering] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null); // 'speed' | 'quality' | null
  const [showLargeIndicator, setShowLargeIndicator] = useState(false);
  const [indicatorType, setIndicatorType] = useState('play'); // 'play' | 'pause'
  const indicatorKeyRef = useRef(0);
  const [indicatorKey, setIndicatorKey] = useState(0);

  // Ref for auto-hiding controls
  const controlsTimeoutRef = useRef(null);
  const indicatorTimerRef = useRef(null);

  // Trigger indicator - uses a stable counter key to re-mount animation only once
  const triggerIndicator = (type) => {
    setIndicatorType(type);
    indicatorKeyRef.current += 1;
    setIndicatorKey(indicatorKeyRef.current);
    setShowLargeIndicator(true);
    // Auto-hide after animation completes (600ms matches the CSS animation duration)
    if (indicatorTimerRef.current) clearTimeout(indicatorTimerRef.current);
    indicatorTimerRef.current = setTimeout(() => {
      setShowLargeIndicator(false);
    }, 650);
  };

  // Reset/Trigger controls timer
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
        setActiveMenu(null);
      }, 2500);
    }
  }, [isPlaying]);

  // Handle Play/Pause
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      triggerIndicator('pause');
    } else {
      videoRef.current.play().catch(() => {});
      triggerIndicator('play');
    }
    resetControlsTimeout();
  }, [isPlaying, resetControlsTimeout]);

  // Handle Fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error('Fullscreen request failed:', err);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  // Handle Volume change
  const handleVolumeChange = (e) => {
    const newVol = parseFloat(e.target.value);
    setVolume(newVol);
    if (videoRef.current) {
      videoRef.current.volume = newVol;
      videoRef.current.muted = newVol === 0;
    }
    setIsMuted(newVol === 0);
    resetControlsTimeout();
  };

  // Toggle Mute
  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    const nextMuted = !isMuted;
    videoRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
    if (!nextMuted && volume === 0) {
      setVolume(0.5);
      videoRef.current.volume = 0.5;
    }
    resetControlsTimeout();
  }, [isMuted, volume, resetControlsTimeout]);

  // Rewind (Quay lại 5s) & Fast Forward (Tua 5s)
  const handleRewind = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  const handleFastForward = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 5);
    resetControlsTimeout();
  }, [duration, resetControlsTimeout]);

  // Timeline Progress Drag/Click Seek (Tua)
  const handleSeek = useCallback((e) => {
    if (!videoRef.current || !timelineRef.current || duration === 0) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const clickPercent = Math.max(0, Math.min(1, clickX / width));
    const newTime = clickPercent * duration;

    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    resetControlsTimeout();
  }, [duration, resetControlsTimeout]);

  // Mobile Touch support for seek
  const handleTouchSeek = useCallback((e) => {
    if (!videoRef.current || !timelineRef.current || duration === 0) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const touch = e.touches[0] || e.changedTouches[0];
    const clickX = touch.clientX - rect.left;
    const width = rect.width;
    const clickPercent = Math.max(0, Math.min(1, clickX / width));
    const newTime = clickPercent * duration;

    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    resetControlsTimeout();
  }, [duration, resetControlsTimeout]);

  // Dragging Timeline State
  const [isDragging, setIsDragging] = useState(false);

  const handleTimelineMouseDown = (e) => {
    setIsDragging(true);
    handleSeek(e);
    resetControlsTimeout();
  };

  const handleTimelineTouchStart = (e) => {
    setIsDragging(true);
    handleTouchSeek(e);
    resetControlsTimeout();
  };

  const handleTimelineMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleSeek(e);
  }, [isDragging, handleSeek]);

  const handleTimelineTouchMove = useCallback((e) => {
    if (!isDragging) return;
    handleTouchSeek(e);
  }, [isDragging, handleTouchSeek]);

  const handleTimelineMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleTimelineMouseMove);
      window.addEventListener('mouseup', handleTimelineMouseUp);
      window.addEventListener('touchmove', handleTimelineTouchMove);
      window.addEventListener('touchend', handleTimelineMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleTimelineMouseMove);
      window.removeEventListener('mouseup', handleTimelineMouseUp);
      window.removeEventListener('touchmove', handleTimelineTouchMove);
      window.removeEventListener('touchend', handleTimelineMouseUp);
    };
  }, [isDragging, handleTimelineMouseMove, handleTimelineTouchMove, handleTimelineMouseUp]);

  // Adjust speed rate
  const changeSpeed = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
    setActiveMenu(null);
    resetControlsTimeout();
  };

  // Adjust quality (mock switching)
  const changeQuality = (qOption) => {
    setQuality(qOption);
    setIsBuffering(true);
    setActiveMenu(null);
    
    // Simulate YouTube network buffering spinner
    setTimeout(() => {
      setIsBuffering(false);
    }, 700);
    resetControlsTimeout();
  };

  // Video Events
  const onTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const onLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const onPlay = () => setIsPlaying(true);
  const onPause = () => setIsPlaying(false);
  const onWaiting = () => setIsBuffering(true);
  const onPlaying = () => setIsBuffering(false);

  // Keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Avoid conflicts if focused on input elements
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
        return;
      }

      resetControlsTimeout();

      switch (e.key.toLowerCase()) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'arrowright':
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 5);
          }
          break;
        case 'arrowleft':
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
          }
          break;
        case 'arrowup':
          e.preventDefault();
          setVolume(prev => {
            const next = Math.min(1, prev + 0.1);
            if (videoRef.current) {
              videoRef.current.volume = next;
              videoRef.current.muted = next === 0;
            }
            setIsMuted(next === 0);
            return next;
          });
          break;
        case 'arrowdown':
          e.preventDefault();
          setVolume(prev => {
            const next = Math.max(0, prev - 0.1);
            if (videoRef.current) {
              videoRef.current.volume = next;
              videoRef.current.muted = next === 0;
            }
            setIsMuted(next === 0);
            return next;
          });
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, toggleFullscreen, toggleMute, duration, resetControlsTimeout]);

  // Monitor fullscreen events to keep state sync
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Manage control timer on state changes
  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isPlaying, resetControlsTimeout]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <PlayerContainer
      ref={containerRef}
      showControls={showControls}
      onMouseMove={resetControlsTimeout}
      onMouseLeave={() => {
        if (isPlaying) {
          setShowControls(false);
          setActiveMenu(null);
        }
      }}
    >
      <StyledVideo
        ref={videoRef}
        src={src}
        autoPlay
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onPlay={onPlay}
        onPause={onPause}
        onWaiting={onWaiting}
        onPlaying={onPlaying}
      />

      {/* Buffering/Loading Indicator */}
      {isBuffering && (
        <BufferingOverlay>
          <LoadingOutlined style={{ fontSize: 50, color: '#ff0000' }} spin />
        </BufferingOverlay>
      )}

      {/* Ripple Play/Pause indicator on double click / action */}
      {showLargeIndicator && (
        <LargePlayPauseIndicator key={indicatorKey}>
          {indicatorType === 'play' ? <CaretRightFilled /> : <PauseOutlined />}
        </LargePlayPauseIndicator>
      )}

      {/* Settings speed dropdown */}
      {activeMenu === 'speed' && (
        <DropdownMenu right="48px">
          <MenuItem disabled style={{ background: 'rgba(255,255,255,0.05)', color: '#aaa', fontWeight: 'bold', fontSize: 11 }}>TỐC ĐỘ PHÁT</MenuItem>
          {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
            <MenuItem
              key={rate}
              active={playbackRate === rate}
              onClick={() => changeSpeed(rate)}
            >
              {rate === 1 ? 'Chuẩn' : `${rate}x`}
            </MenuItem>
          ))}
        </DropdownMenu>
      )}

      {/* Settings quality dropdown */}
      {activeMenu === 'quality' && (
        <DropdownMenu right="16px">
          <MenuItem disabled style={{ background: 'rgba(255,255,255,0.05)', color: '#aaa', fontWeight: 'bold', fontSize: 11 }}>CHẤT LƯỢNG MẠNG</MenuItem>
          {['Auto', '1080p', '720p', '480p', '360p'].map(qOption => (
            <MenuItem
              key={qOption}
              active={quality === qOption}
              onClick={() => changeQuality(qOption)}
            >
              {qOption}
            </MenuItem>
          ))}
        </DropdownMenu>
      )}

      {/* Custom Control Bar overlay */}
      <ControlsOverlay visible={showControls || !isPlaying}>
        {/* Timeline Progress */}
        <TimelineContainer
          ref={timelineRef}
          onMouseDown={handleTimelineMouseDown}
          onTouchStart={handleTimelineTouchStart}
        >
          <TimelineProgress percent={progressPercent} />
          <TimelineHandle percent={progressPercent} />
        </TimelineContainer>

        {/* Buttons Row */}
        <ControlsRow>
          {/* Left Buttons Group */}
          <ControlsGroup>
            <ControlButton onClick={togglePlay} title={isPlaying ? 'Tạm dừng (Space)' : 'Phát (Space)'}>
              {isPlaying ? (
                <PauseCircleFilled style={{ fontSize: 22 }} />
              ) : (
                <PlayCircleFilled style={{ fontSize: 22 }} />
              )}
            </ControlButton>

            <ControlButton onClick={handleRewind} title="Quay lại 5 giây (←)">
              <BackwardOutlined style={{ fontSize: 20 }} />
            </ControlButton>

            <ControlButton onClick={handleFastForward} title="Tua đi 5 giây (→)">
              <ForwardOutlined style={{ fontSize: 20 }} />
            </ControlButton>

            {/* Volume controls */}
            <VolumeContainer>
              <ControlButton onClick={toggleMute} title={isMuted ? 'Mở tiếng (m)' : 'Tắt tiếng (m)'}>
                <SoundOutlined
                  style={{
                    fontSize: 18,
                    color: isMuted ? '#888' : '#fff'
                  }}
                />
              </ControlButton>
              <VolumeSlider
                type="range"
                className="volume-slider"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
              />
            </VolumeContainer>

            {/* Time display */}
            <TimeDisplay>
              {formatTime(currentTime)} / {formatTime(duration)}
            </TimeDisplay>
          </ControlsGroup>

          {/* Right Buttons Group */}
          <ControlsGroup>
            {/* Speed selection */}
            <ControlButton
              title="Tốc độ phát"
              onClick={() => setActiveMenu(activeMenu === 'speed' ? null : 'speed')}
              style={{ fontSize: 13, color: activeMenu === 'speed' ? '#ff0000' : '#fff' }}
            >
              Speed: {playbackRate === 1 ? 'Chuẩn' : `${playbackRate}x`}
            </ControlButton>

            {/* Quality selection */}
            <ControlButton
              title="Chất lượng"
              onClick={() => setActiveMenu(activeMenu === 'quality' ? null : 'quality')}
              style={{ fontSize: 13, color: activeMenu === 'quality' ? '#ff0000' : '#fff' }}
            >
              <SettingOutlined style={{ fontSize: 16, marginRight: 4 }} />
              {quality}
            </ControlButton>

            {/* Fullscreen button */}
            <ControlButton onClick={toggleFullscreen} title={isFullscreen ? 'Thoát toàn màn hình (f)' : 'Toàn màn hình (f)'}>
              {isFullscreen ? (
                <FullscreenExitOutlined style={{ fontSize: 18 }} />
              ) : (
                <FullscreenOutlined style={{ fontSize: 18 }} />
              )}
            </ControlButton>
          </ControlsGroup>
        </ControlsRow>
      </ControlsOverlay>
    </PlayerContainer>
  );
};

export default YoutubeVideoPlayer;
